import { useEffect, useState } from "react";

function mapToBinanceSymbol(symbol) {
  if (symbol?.toUpperCase().endsWith("USD")) {
    return symbol?.toUpperCase().replace("USD", "USDT");
  }
  return symbol?.toUpperCase();
}

function normalizeOrders(orders, isDescending = true) {
  const merged = [];

  orders?.forEach((o) => {
    const existing = merged.find((m) => m.price === o.price);
    if (existing) existing.quantity += o.quantity;
    else merged.push({ ...o });
  });

  merged.sort((a, b) =>
    isDescending ? b.price - a.price : a.price - b.price
  );

  // Keep top 50 only
  return merged.slice(0, 50);
}

export function useBinanceOrderBook(symbol) {
  const [orderBook, setOrderBook] = useState({
    bids: [],
    asks: [],
    symbol: "",
  });

  useEffect(() => {
    if (!symbol) return;

    const binanceSymbol = mapToBinanceSymbol(symbol);
    let ws;
    let book = { bids: [], asks: [] };
    let lastEmit = 0;

    /** 1️⃣ FETCH INITIAL SNAPSHOT */
    fetch(`https://api.binance.com/api/v3/depth?symbol=${binanceSymbol}&limit=50`)
      .then((res) => res.json())
      .then((snapshot) => {
        book.bids = snapshot?.bids?.map(([p, q]) => ({
          price: Number(p),
          quantity: Number(q),
        }));
        book.asks = snapshot?.asks?.map(([p, q]) => ({
          price: Number(p),
          quantity: Number(q),
        }));

        setOrderBook({
          bids: normalizeOrders(book.bids, true),
          asks: normalizeOrders(book.asks, false),
          symbol,
        });
      })
      .catch((err) => {
        console.error("Spot orderbook fetch error:", err);
        setOrderBook({ bids: [], asks: [], symbol });
      });

    /** 2️⃣ WEBSOCKET SPOT */
    ws = new WebSocket(
      `wss://stream.binance.com:9443/ws/${binanceSymbol.toLowerCase()}@depth@100ms`
    );

    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);

      msg.b?.forEach(([p, q]) => {
        const price = Number(p);
        const quantity = Number(q);
        const idx = book.bids.findIndex((o) => o.price === price);
        if (quantity > 0) {
          idx >= 0
            ? (book.bids[idx].quantity = quantity)
            : book.bids.push({ price, quantity });
        } else if (idx >= 0) {
          book.bids.splice(idx, 1);
        }
      });

      msg.a?.forEach(([p, q]) => {
        const price = Number(p);
        const quantity = Number(q);
        const idx = book.asks.findIndex((o) => o.price === price);
        if (quantity > 0) {
          idx >= 0
            ? (book.asks[idx].quantity = quantity)
            : book.asks.push({ price, quantity });
        } else if (idx >= 0) {
          book.asks.splice(idx, 1);
        }
      });

      const now = Date.now();
      if (now - lastEmit > 500) {
        setOrderBook({
          bids: normalizeOrders(book.bids, true),
          asks: normalizeOrders(book.asks, false),
          symbol,
        });
        lastEmit = now;
      }
    };

    ws.onerror = () => ws.close();

    ws.onclose = () => {
      // auto-reconnect after 2s
      setTimeout(() => {
        if (symbol) {
          ws = new WebSocket(
            `wss://stream.binance.com:9443/ws/${binanceSymbol.toLowerCase()}@depth@100ms`
          );
        }
      }, 2000);
    };

    return () => ws?.close();
  }, [symbol]);

  return orderBook;
}
